/**
 * Storeit — Serien-Scroll
 *
 * Auf dem Desktop (>= 750px) wird die horizontale Spur per Seiten-Scroll bewegt,
 * während der Inhalt mit `position: sticky` gepinnt bleibt.
 * Auf Mobilgeräten und bei `prefers-reduced-motion: reduce` passiert nichts —
 * dann greift das reine CSS-Swipe-Verhalten (scroll-snap).
 */

const DESKTOP_MEDIA_QUERY = '(min-width: 750px)';
const REDUCED_MOTION_MEDIA_QUERY = '(prefers-reduced-motion: reduce)';
const PINNED_CLASS = 'storeit-series-scroll--pinned';


/**
 * Horizon scrollt NICHT am window: <html> hat overflow-y: hidden und der
 * eigentliche Scroll-Container ist `div.page-wrapper`. Deshalb muss der
 * Scroll-Listener am tatsächlichen Container hängen, sonst feuert er nie.
 */
function findScrollContainer(el) {
  let node = el.parentElement;
  while (node && node !== document.body && node !== document.documentElement) {
    const overflowY = getComputedStyle(node).overflowY;
    if (/(auto|scroll)/.test(overflowY) && node.scrollHeight > node.clientHeight + 4) {
      return node;
    }
    node = node.parentElement;
  }
  return window;
}

class StoreitSeriesScroll extends HTMLElement {
  #track = null;
  #viewport = null;
  #desktopQuery = null;
  #reducedMotionQuery = null;
  #resizeObserver = null;
  #frame = null;
  #viewportFrame = null;
  #distance = 0;
  #active = false;
  #scroller = window;
  #prev = null;
  #next = null;

  connectedCallback() {
    this.#track = this.querySelector('[data-storeit-track]');
    this.#viewport = this.querySelector('[data-storeit-viewport]');

    if (!this.#track || !this.#viewport) return;

    this.#prev = this.querySelector('[data-storeit-prev]');
    this.#next = this.querySelector('[data-storeit-next]');

    if (this.#prev) this.#prev.addEventListener('click', this.#handlePrev);
    if (this.#next) this.#next.addEventListener('click', this.#handleNext);
    this.#viewport.addEventListener('scroll', this.#updateNav, { passive: true });

    this.#desktopQuery = window.matchMedia(DESKTOP_MEDIA_QUERY);
    this.#reducedMotionQuery = window.matchMedia(REDUCED_MOTION_MEDIA_QUERY);

    this.#scroller = findScrollContainer(this);
    this.#scroller.addEventListener('scroll', this.#handleScroll, { passive: true });
    // Fallback: manche Layouts scrollen doch am window
    if (this.#scroller !== window) {
      window.addEventListener('scroll', this.#handleScroll, { passive: true });
    }
    window.addEventListener('resize', this.#handleResize, { passive: true });
    this.#addMediaListener(this.#desktopQuery);
    this.#addMediaListener(this.#reducedMotionQuery);

    if ('ResizeObserver' in window) {
      this.#resizeObserver = new ResizeObserver(this.#handleResize);
      this.#resizeObserver.observe(this.#track);
    }

    this.#measure();
  }

  disconnectedCallback() {
    if (this.#scroller) this.#scroller.removeEventListener('scroll', this.#handleScroll);
    window.removeEventListener('scroll', this.#handleScroll);
    window.removeEventListener('resize', this.#handleResize);
    this.#removeMediaListener(this.#desktopQuery);
    this.#removeMediaListener(this.#reducedMotionQuery);

    if (this.#prev) this.#prev.removeEventListener('click', this.#handlePrev);
    if (this.#next) this.#next.removeEventListener('click', this.#handleNext);
    if (this.#viewport) this.#viewport.removeEventListener('scroll', this.#updateNav);

    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
      this.#resizeObserver = null;
    }

    if (this.#frame) {
      cancelAnimationFrame(this.#frame);
      this.#frame = null;
    }

    this.#reset();
  }

  #addMediaListener(query) {
    if (!query) return;

    if (typeof query.addEventListener === 'function') {
      query.addEventListener('change', this.#handleResize);
    } else if (typeof query.addListener === 'function') {
      // Safari < 14
      query.addListener(this.#handleResize);
    }
  }

  #removeMediaListener(query) {
    if (!query) return;

    if (typeof query.removeEventListener === 'function') {
      query.removeEventListener('change', this.#handleResize);
    } else if (typeof query.removeListener === 'function') {
      query.removeListener(this.#handleResize);
    }
  }

  #step() {
    const card = this.#track ? this.#track.firstElementChild : null;
    if (!card) return this.#viewport ? this.#viewport.clientWidth : 0;
    const gap = parseFloat(getComputedStyle(this.#track).columnGap || '0') || 0;
    return card.getBoundingClientRect().width + gap;
  }

  // Im gepinnten Modus (Desktop) wird die Reihe ueber den Seiten-Scroll bewegt:
  // ein Klick scrollt die Seite so weit, dass die Reihe um eine Karte weiterlaeuft.
  #pinnedScrollable() {
    return this.getBoundingClientRect().height - this.#viewportHeight();
  }

  #pinnedProgress() {
    const scrollable = this.#pinnedScrollable();
    if (scrollable <= 0) return 0;
    return Math.min(Math.max(-this.getBoundingClientRect().top / scrollable, 0), 1);
  }

  #move(direction) {
    if (!this.#viewport) return;

    if (this.#active && this.#distance > 0) {
      const scrollable = this.#pinnedScrollable();
      const deltaX = Math.min(this.#step(), this.#distance);
      const deltaY = (deltaX / this.#distance) * scrollable * direction;
      const target = this.#scroller === window ? window : this.#scroller;
      target.scrollBy({ top: deltaY, behavior: 'smooth' });
      return;
    }

    this.#animateViewport(this.#step() * direction);
  }

  // Eigene Animation statt scrollBy({behavior:'smooth'}): Chrome bricht die
  // native Smooth-Scroll-Animation bei scroll-snap + Seiten-Scroll-Container ab.
  #animateViewport(delta) {
    const viewport = this.#viewport;
    if (!viewport) return;

    const max = viewport.scrollWidth - viewport.clientWidth;
    const step = this.#step() || 1;
    const start = viewport.scrollLeft;
    const rawTarget = Math.min(Math.max(start + delta, 0), max);
    // Auf Kartenanfang runden, damit das Ergebnis dem Snap entspricht.
    const target = Math.min(Math.round(rawTarget / step) * step, max);
    const distance = target - start;
    if (Math.abs(distance) < 1) return;

    if (this.#viewportFrame) cancelAnimationFrame(this.#viewportFrame);

    const duration = 420;
    const startTime = performance.now();
    const previousSnap = viewport.style.scrollSnapType;
    viewport.style.scrollSnapType = 'none';

    const tick = (now) => {
      const t = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      viewport.scrollLeft = start + distance * eased;
      if (t < 1) {
        this.#viewportFrame = requestAnimationFrame(tick);
      } else {
        this.#viewportFrame = null;
        viewport.style.scrollSnapType = previousSnap;
        this.#updateNav();
      }
    };

    this.#viewportFrame = requestAnimationFrame(tick);
  }

  #handlePrev = () => {
    this.#move(-1);
  };

  #handleNext = () => {
    this.#move(1);
  };

  #updateNav = () => {
    if (!this.#viewport || !this.#prev || !this.#next) return;

    if (this.#active && this.#distance > 0) {
      const progress = this.#pinnedProgress();
      this.#prev.disabled = progress <= 0.001;
      this.#next.disabled = progress >= 0.999;
      return;
    }

    const max = this.#viewport.scrollWidth - this.#viewport.clientWidth;
    const x = this.#viewport.scrollLeft;
    // Mobil: Wisch-Hinweis ausblenden, sobald gewischt wurde, Fortschritt fuer die Leiste.
    if (x > 8) this.classList.add('is-swiped');
    this.style.setProperty('--storeit-progress', max > 0 ? String(Math.min(1, x / max)) : '1');
    this.#prev.disabled = x <= 1;
    this.#next.disabled = x >= max - 1;
  };

  #handleResize = () => {
    this.#measure();
  };

  #handleScroll = () => {
    if (!this.#active || this.#frame) return;

    this.#frame = requestAnimationFrame(() => {
      this.#frame = null;
      this.#render();
    });
  };

  #measure() {
    if (!this.#track || !this.#viewport) return;

    const wantsPinned = this.dataset.scrollMode === 'pinned';
    const isDesktop = this.#desktopQuery ? this.#desktopQuery.matches : false;
    const prefersReducedMotion = this.#reducedMotionQuery ? this.#reducedMotionQuery.matches : false;

    if (!wantsPinned || !isDesktop || prefersReducedMotion) {
      this.#active = false;
      this.#reset();
      return;
    }

    const viewportHeight = this.#viewportHeight();
    const distance = Math.max(0, this.#track.scrollWidth - this.#viewport.clientWidth);

    if (distance === 0) {
      this.#active = false;
      this.#reset();
      this.#updateNav();
      return;
    }

    this.#updateNav();
    this.#distance = distance;
    this.#active = true;
    this.classList.add(PINNED_CLASS);
    this.style.height = `${viewportHeight + distance}px`;
    this.#render();
  }

  #render() {
    if (!this.#active || !this.#track) return;

    const rect = this.getBoundingClientRect();
    const scrollable = rect.height - this.#viewportHeight();
    const progress = scrollable > 0 ? Math.min(Math.max(-rect.top / scrollable, 0), 1) : 0;

    this.#track.style.transform = `translate3d(${-progress * this.#distance}px, 0, 0)`;
    this.#updateNav();
  }

  #viewportHeight() {
    return this.#scroller && this.#scroller !== window
      ? this.#scroller.clientHeight
      : window.innerHeight;
  }

  #reset() {
    this.classList.remove(PINNED_CLASS);
    this.#updateNav();
    this.style.height = '';

    if (this.#track) {
      this.#track.style.transform = '';
    }
  }
}

if (!customElements.get('storeit-series-scroll')) {
  customElements.define('storeit-series-scroll', StoreitSeriesScroll);
}
